import { compact, constant, equals, find, flow, head, isObject, map, pick, pickBy, pipe, sortBy } from 'lodash/fp';
import { mapDto } from '../../../../../../types/DtoEntity';
import { getDefaultQuestionnaire } from '../../../ReviewCycleCreate/services/defaultQuestionnaire';
import { fromReviewQuestionnaire } from '../../../ReviewQuestionnaireAdminPage/codec/reviewQuestionnaireDecoder';

export const questionnaireToTemplateSections = questionnaire => {
    return compact([
        questionnaire.graph.enabled && {
            id: 'graph',
            type: 'Graph',
            data: questionnaire.graph,
        },
        ...questionnaire.sections,
        questionnaire.secondaryOverallRatings.enabled && {
            id: 'secondaryOverallRating',
            type: 'SecondaryOverallRating',
            data: questionnaire.secondaryOverallRatings,
        },
        questionnaire.overallRatings.enabled && {
            id: 'overallRating',
            type: 'OverallRating',
            data: questionnaire.overallRatings,
        },
    ]);
};

/**
 * @returns LegacyReviewQuestionnaire with extra templateId field.
 */
export const templateSectionsToQuestionnaire = (cycleType, templateSections, templateId) => {
    const defaultQuestionnaire = getDefaultQuestionnaire();
    const graph = find(section => section.type === 'Graph', templateSections)?.data || {};
    const overallRatings = find(section => section.type === 'OverallRating', templateSections)?.data || {};
    const secondaryOverallRatings =
        find(section => section.type === 'SecondaryOverallRating', templateSections)?.data || {};
    const sections = templateSections.filter(
        section =>
            section.type !== 'Graph' && section.type !== 'OverallRating' && section.type !== 'SecondaryOverallRating'
    );

    const noTemplateAndCalibration = !templateId && cycleType === 'CALIBRATION';

    return {
        graph: {
            ...defaultQuestionnaire.graph,
            ...graph,
            enabled: !!graph?.enabled,
        },
        overallRatings: {
            ...defaultQuestionnaire.overallRatings,
            ...overallRatings,
            enabled: !!(
                overallRatings.enabled ??
                (noTemplateAndCalibration ? true : defaultQuestionnaire.overallRatings.enabled)
            ),
        },
        secondaryOverallRatings: {
            ...defaultQuestionnaire.secondaryOverallRatings,
            ...secondaryOverallRatings,
            enabled: !!(
                secondaryOverallRatings.enabled ??
                (noTemplateAndCalibration ? true : defaultQuestionnaire.secondaryOverallRatings.enabled)
            ),
        },
        sections,
        templateId,
    };
};

const toSection = (id, type, data) => {
    return data.enabled
        ? [
              {
                  id,
                  type,
                  data,
              },
          ]
        : [];
};

const toSectionWithEditModeFlag = (id, type, data) => {
    data.inEditMode = false;
    return toSection(id, type, data);
};

const toFrontendTemplate = ({ id, value: [meta, legacyReview] }) => {
    const graphSection = toSection('graph', 'Graph', legacyReview.graph);
    const overallRatingSection = toSectionWithEditModeFlag(
        'overallRating',
        'OverallRating',
        legacyReview.overallRatings
    );
    const secondaryOverallRatingSection = toSectionWithEditModeFlag(
        'secondaryOverallRating',
        'SecondaryOverallRating',
        legacyReview.secondaryOverallRatings
    );

    return {
        id,
        title: meta.name,
        description: meta.description,
        sections: [
            ...graphSection,
            ...legacyReview.sections,
            ...secondaryOverallRatingSection,
            ...overallRatingSection,
        ],
    };
};

const managerRevieweeNormalizer = questionType => managerOrRevieweeData => {
    switch (questionType) {
        case 'TEXT': {
            const toPick = managerOrRevieweeData.ratingsEnabled ? ['question', 'ratings'] : ['question'];
            return pick(toPick, managerOrRevieweeData);
        }
        case 'SCALES': {
            const questionConfig = pick(['allowComment', 'question'], managerOrRevieweeData);
            // Sometimes we duplicate scale and ratings, in which case we need to pick one.
            const scale = head(Object.values(pick(['ratings', 'scale'], managerOrRevieweeData))).map(s => {
                // Mismatch between backend and frontend
                if (isObject(s)) {
                    return s.text;
                } else {
                    return s;
                }
            });
            return [questionConfig, scale];
        }
        case 'SLIDER': {
            const questionConfig = pick(['allowComment', 'question'], managerOrRevieweeData);
            return [questionConfig];
        }
        case 'CHECKBOXES': {
            const questionConfig = pick(['allowComment', 'question'], managerOrRevieweeData);
            const options = head(Object.values(pick(['ratings', 'scale'], managerOrRevieweeData))).map(s => {
                // Mismatch between backend and frontend
                if (isObject(s)) {
                    return s.text;
                } else {
                    return s;
                }
            });
            return [questionConfig, options];
        }
    }
    return managerOrRevieweeData;
};

const sectionNormalizer = section => {
    if (section.type === 'Graph') {
        section = { ...section, questionType: 'GRAPH' };
    }
    if (section.type === 'OverallRating') {
        section = { ...section, questionType: 'OVERALL_RATING' };
    }

    switch (section.questionType) {
        case 'HEADING':
            return pick(['description', 'title'], section);
        case 'TEXT': {
            // We pick fields that are true since false is default (and implicit if field not specified)
            const questionMeta = pipe(
                pickBy(Boolean),
                pick(['confidentialTopic', 'requiredForManager', 'requiredForReviewee'])
            )(section);
            const questionData = [section.manager, section.reviewee].map(
                managerRevieweeNormalizer(section.questionType)
            );
            return [questionMeta, questionData];
        }
        case 'SCALES': {
            // We pick fields that are true since false is default (and implicit if field not specified)
            const questionMeta = pipe(
                pickBy(Boolean),
                pick(['confidentialTopic', 'requiredForManager', 'requiredForReviewee'])
            )(section);
            const questionData = [section.manager, section.reviewee].map(
                managerRevieweeNormalizer(section.questionType)
            );
            return [questionMeta, questionData];
        }
        case 'SLIDER': {
            // We pick fields that are true since false is default (and implicit if field not specified)
            const questionMeta = pipe(
                pickBy(Boolean),
                pick(['confidentialTopic', 'requiredForManager', 'requiredForReviewee', 'sliderLabels', 'sliderSteps'])
            )(section);
            const questionData = [section.manager, section.reviewee].map(
                managerRevieweeNormalizer(section.questionType)
            );
            return [questionMeta, questionData];
        }
        case 'CHECKBOXES': {
            // We pick fields that are true since false is default (and implicit if field not specified)
            const questionMeta = pipe(
                pickBy(Boolean),
                pick(['confidentialTopic', 'requiredForManager', 'requiredForReviewee'])
            )(section);
            const questionData = [section.manager, section.reviewee].map(
                managerRevieweeNormalizer(section.questionType)
            );
            return [questionMeta, questionData];
        }
        case 'DROPDOWN': {
            // Not used by templates
            return [];
        }
        case 'GRAPH': {
            return section;
        }
        case 'OVERALL_RATING': {
            return pipe(
                pickBy(Boolean),
                pick([
                    'confidential',
                    'enabledForManager',
                    'enabledForReviewee',
                    'ratings',
                    'requiredForManager',
                    'requiredForReviewee',
                ])
            )(section.data);
        }
    }
};

export function areTemplateSectionsEqual(templateSectionsA, templateSectionsB) {
    try {
        return equals(templateSectionsA.map(sectionNormalizer), templateSectionsB.map(sectionNormalizer));
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error('failed to match template due to an error', e);
        return false;
    }
}

export function getCompanyTemplates(questionnaires, defaultQuestionnaire) {
    return flow(
        constant(questionnaires),
        sortBy(x => x.value.order),
        map(mapDto(fromReviewQuestionnaire(defaultQuestionnaire))),
        map(toFrontendTemplate)
    )();
}
